import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

import { Button } from "./Button";
import { BellIcon } from "./icons";
import { Banner, EmptyState } from "./states";
import { StatusPill } from "./StatusPill";

/**
 * The one notifications mechanism: a bell whose hover preview shows the
 * newest few, and a page body that lists everything with bulk mark-read.
 *
 * Presentational only — no Firebase, no react-router. Each site keeps its own
 * Firestore listener and its own `read` write; this file only ever asks the
 * caller to do those things (`onOpen`, `onMarkRead`) and renders what it is
 * given. That is what lets the contractor and user sites, which disagree on
 * almost everything else, render the exact same bell and the exact same page.
 */

/** One notification, already shaped by the site: the fallback title is
 *  applied, the timestamp is pre-formatted, and the destination is resolved. */
export interface NotificationEntry {
  id: string; // Firestore doc id
  title: string; // site already applied its fallback ("Update")
  body?: string;
  read: boolean;
  when: string; // pre-formatted by the site; may be ""
  href: string | null; // destination; null = nowhere to go
}

/** What a caller's `renderLink` is handed — enough to build a router `<Link>`
 *  (or, with no `renderLink`, for `PlainLink` to build a plain `<a>`). */
export interface NotificationLinkProps {
  href: string;
  className: string;
  children: ReactNode;
  onClick?: () => void | Promise<unknown>;
  "aria-label"?: string;
  "aria-current"?: "page";
}

/** Newest-first entries; the preview shows only the first this many. */
export const NOTIFICATION_PREVIEW_COUNT = 5;

/**
 * The default link renderer, used when a caller passes no `renderLink` — the
 * plain-anchor `map.html` document, which has no router to hand it one.
 *
 * A plain anchor's click is a full-document navigation, which would tear the
 * page down — taking an in-flight Firestore write with it — before the write
 * leaves the tab. So for an unmodified primary click we run `onClick` first
 * and only then hand off to `window.location.assign`. A modified click
 * (new-tab, new-window, …) just fires `onClick` and lets the browser do what
 * a modified click always does.
 */
function PlainLink({ href, className, children, onClick, ...rest }: NotificationLinkProps): JSX.Element {
  const handleClick = onClick
    ? (event: ReactMouseEvent<HTMLAnchorElement>) => {
        const plainClick =
          event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
        if (!plainClick) {
          void onClick();
          return;
        }
        event.preventDefault();
        Promise.resolve(onClick()).finally(() => window.location.assign(href));
      }
    : undefined;

  return (
    <a href={href} className={className} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}

/**
 * The bell: always a link (clicking it navigates to the notifications page —
 * no toggle), plus a hover-only preview of the newest five.
 *
 * Hover is mouse-only and skipped entirely on the page the preview would
 * duplicate (`current`): touch taps and keyboard Enter both just navigate,
 * which lands on a page with the same content and more. Hover-intent timers
 * (open after 120ms, close 200ms after the pointer leaves) are what let the
 * pointer cross the gap between the bell and the panel below it — the panel
 * is a DOM child but sits outside the bell's own box, so the pointer leaves
 * the wrapper's hit area in that gap and `pointerenter` re-fires when it
 * reaches the panel; the close timer queued for the gap is still pending and
 * gets cleared before it fires.
 */
export function NotificationBellPreview({
  entries,
  href,
  current = false,
  onOpen,
  renderLink,
}: {
  entries: readonly NotificationEntry[]; // newest first, full feed (unread count is computed from ALL of it)
  href: string; // the notifications page
  current?: boolean; // true on the notifications page
  onOpen: (entry: NotificationEntry) => void | Promise<unknown>; // mark that one read
  renderLink?: (props: NotificationLinkProps) => ReactNode; // default: PlainLink
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | null>(null);

  function clearTimer() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function close() {
    clearTimer();
    setOpen(false);
  }

  // Belt-and-braces: a pending timer must never fire after this unmounts.
  useEffect(() => clearTimer, []);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A click on the bell itself first fires a genuine pointerenter (moving the
  // mouse onto it is how a click gets there), which can schedule the 120ms
  // open-timer a moment before the click navigates. Without this, the timer
  // does not know the destination is now `current` and pops the preview open
  // right after landing on the page it is a duplicate of. This component
  // never unmounts across a client-routed navigation (the bell sits outside
  // the route's `<Outlet/>`), so `current` flipping true is the only signal
  // available that the trip just ended.
  useEffect(() => {
    if (current) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  function onPointerEnter(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "mouse" || current) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpen(true);
    }, 120);
  }

  function onPointerLeave(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "mouse") return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpen(false);
    }, 200);
  }

  const unread = entries.reduce((count, entry) => count + (entry.read ? 0 : 1), 0);
  const label = unread > 0 ? `Notifications, ${unread} unread` : "Notifications";
  const Link = renderLink ?? PlainLink;

  const bellProps: NotificationLinkProps = {
    href,
    className: "icon-btn",
    "aria-label": label,
    children: (
      <>
        <BellIcon />
        {unread > 0 ? (
          <span className="icon-btn__dot" aria-hidden="true">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </>
    ),
    ...(current ? { "aria-current": "page" as const } : {}),
  };

  return (
    <div className="combobox" onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
      {Link(bellProps)}
      {open ? (
        <div className="menu menu--end menu--preview" role="region" aria-label="Latest notifications">
          {entries.length === 0 ? (
            <EmptyState title="Nothing yet">
              Updates about your jobs and bookings will show up here.
            </EmptyState>
          ) : (
            <ul className="stack stack--tight">
              {entries.slice(0, NOTIFICATION_PREVIEW_COUNT).map((entry) => {
                const body = (
                  <span className="stack stack--tight">
                    <span className="row__top">
                      <span className="row__title">{entry.title}</span>
                      <span className="cluster cluster--tight">
                        {!entry.read ? <StatusPill tone="progress">New</StatusPill> : null}
                        {entry.when ? <span className="row__when">{entry.when}</span> : null}
                      </span>
                    </span>
                    {entry.body ? <span className="row__preview">{entry.body}</span> : null}
                  </span>
                );
                return (
                  <li key={entry.id}>
                    {entry.href
                      ? Link({
                          href: entry.href,
                          className: "menu__item",
                          onClick: () => {
                            close();
                            return onOpen(entry);
                          },
                          children: body,
                        })
                      : (
                          <button type="button" className="menu__item" onClick={() => void onOpen(entry)}>
                            {body}
                          </button>
                        )}
                  </li>
                );
              })}
            </ul>
          )}
          <div className="menu__foot">
            {Link({
              href,
              className: "btn btn-secondary btn-sm",
              onClick: close,
              children: "View all",
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The notifications page body: every entry, a per-row Select checkbox and a
 * toolbar to mark the selection read.
 *
 * Renders nothing for an empty list — the site mounts its own `EmptyState`
 * instead of this component, the same way loading and error states are the
 * site's job, not this one's.
 */
export function NotificationList({
  entries,
  onOpen,
  onMarkRead,
  onShowMore,
  renderLink,
}: {
  entries: readonly NotificationEntry[];
  onOpen: (entry: NotificationEntry) => void | Promise<unknown>;
  onMarkRead: (ids: string[]) => Promise<void>; // bulk; REJECTS on failure
  /** Given only while the feed may hold older entries than are loaded — the
   *  site's listener is bounded, so "all of them" is paged, not streamed. */
  onShowMore?: () => void;
  renderLink?: (props: NotificationLinkProps) => ReactNode;
}): JSX.Element {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [message, setMessage] = useState("");
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Ids that vanished from the feed (marked read and dropped off, e.g.) drop
  // out of the selection on their own — `chosen` is derived, never stored.
  const chosen = entries.filter((entry) => selected.has(entry.id));
  const chosenUnread = chosen.filter((entry) => !entry.read);
  const all = entries.length > 0 && chosen.length === entries.length;
  const some = chosen.length > 0 && !all;

  // The shared Checkbox (Field.tsx) takes no ref, so "Select all" is written
  // inline with the same `.checkbox` markup — `indeterminate` has no HTML
  // attribute and can only be set on the element itself.
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = some;
  }, [some]);

  function toggleAll() {
    setSelected(all ? new Set() : new Set(entries.map((entry) => entry.id)));
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function markSelected() {
    setBusy(true);
    setFailed(false);
    const ids = chosenUnread.map((entry) => entry.id);
    try {
      await onMarkRead(ids);
      setSelected(new Set());
      setMessage(`Marked ${ids.length} ${ids.length === 1 ? "notification" : "notifications"} as read`);
    } catch {
      // Keep the selection so the person can just press the button again.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  const Link = renderLink ?? PlainLink;

  return (
    <>
      <div className="select-bar">
        <label className="checkbox">
          <input type="checkbox" ref={selectAllRef} checked={all} onChange={toggleAll} />
          <span>Select all</span>
        </label>
        <span className="cluster cluster--tight">
          {chosen.length > 0 ? <span className="row__when">{chosen.length} selected</span> : null}
          <Button
            variant="secondary"
            size="sm"
            disabled={chosenUnread.length === 0 || busy}
            loading={busy}
            onClick={() => void markSelected()}
          >
            Mark as read
          </Button>
        </span>
      </div>
      <p className="sr-only" role="status">
        {message}
      </p>
      {failed ? <Banner tone="warn">We couldn't mark those as read. Please try again.</Banner> : null}
      <ul className="stack">
        {entries.map((entry) => {
          const body = (
            <span className="stack stack--tight">
              <span className="row__top">
                <span className="row__title">{entry.title}</span>
                {!entry.read ? <StatusPill tone="progress">New</StatusPill> : null}
              </span>
              {entry.body ? <span className="row__preview">{entry.body}</span> : null}
              {entry.when ? <span className="row__sub">{entry.when}</span> : null}
            </span>
          );
          return (
            <li key={entry.id} className="select-row">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={selected.has(entry.id)}
                  onChange={() => toggleOne(entry.id)}
                  // With the time, so two same-titled rows (a redelivered
                  // event writes an identical twin) do not share one name.
                  aria-label={entry.when ? `Select ${entry.title}, ${entry.when}` : `Select ${entry.title}`}
                />
              </label>
              {entry.href
                ? Link({
                    href: entry.href,
                    className: "row",
                    onClick: () => onOpen(entry),
                    children: body,
                  })
                : (
                    <button type="button" className="row" onClick={() => void onOpen(entry)}>
                      {body}
                    </button>
                  )}
            </li>
          );
        })}
      </ul>
      {onShowMore ? (
        <Button variant="secondary" onClick={onShowMore}>
          Show older
        </Button>
      ) : null}
    </>
  );
}
